/**
 * Composable for accessing Communication module translations.
 * 
 * Tries model translations first (models.{prefix}.*), then falls back to
 * communication.* namespace for backwards compatibility.
 */

import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useTranslatable } from '@ja/crud/composables/useTranslatable'

export function useCommunicationTranslations(translationPrefix?: string | null | Ref<string | null | undefined>) {
  const { t, te, locale } = useI18n()
  const { tr } = useTranslatable({ translationPrefix })

  /**
   * Translate a key, trying model translations first, then communication namespace.
   * 
   * @param key - Translation key (e.g., 'schedule_manager.send_now' or 'recipients.title')
   * @param params - Optional parameters for interpolation (e.g., { date: '...', count: 5 })
   * @returns Translated string
   */
  const tm = (key: string, params?: Record<string, string | number>): string => {
    let translation = ''

    // Try model translation first (models.announcement.*)
    if (translationPrefix) {
      const prefix = typeof translationPrefix === 'object' && 'value' in translationPrefix
        ? translationPrefix.value
        : translationPrefix

      if (prefix) {
        const modelKey = `${prefix}.${key}`
        if (te(`models.${modelKey}`)) {
          translation = t(`models.${modelKey}`, params)
        }
      }
    }

    // Fallback to shared Communication model bundle (models.communication.*)
    if (!translation) {
      const sharedKey = `models.communication.${key}`
      if (te(sharedKey)) {
        translation = t(sharedKey, params)
      }
    }

    // Legacy fallback: communication.* namespace (PHP-loaded translations)
    if (!translation) {
      const commKey = `communication.${key}`
      if (te(commKey)) {
        translation = t(commKey, params)
      }
    }

    // Last resort: try with tr() helper
    if (!translation) {
      const trResult = tr(key, translationPrefix)
      if (trResult && trResult !== key.split('.').pop()) {
        translation = trResult
        // Apply params if provided
        if (params) {
          Object.entries(params).forEach(([paramKey, paramValue]) => {
            translation = translation.replace(`:${paramKey}`, String(paramValue))
          })
        }
      }
    }

    // Apply params if translation found and params provided
    if (translation && params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(`:${paramKey}`, String(paramValue))
      })
    }

    // Final fallback: return the key
    return translation || key.split('.').pop() || key
  }

  return { tm, t, tr, locale }
}
